import React from 'react';
import classNames from 'classnames';
import Select, { underlinerFunc } from 'ui/select';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faPlus, faLock, faExclamationTriangle, faSyncAlt } from '@fortawesome/free-solid-svg-icons'

import Data from 'data';
import Store from 'store';
import Locale, { genderString } from 'data/locale';

import ItemSelect from './ItemSelect';
import Tooltip from 'ui/tooltip';
import { D3TooltipItem, D3TooltipGem, D3TooltipSkill, TooltipInput } from 'tooltips';

import './ItemEditor.scss';
import { itemSetAncient, itemSetGemType, itemSetGemLevel, itemSetGemColor, itemChangeStat, itemSetStatValue, itemAddStat, itemSetId, itemSetImported, itemImportReset, itemImportUnlock } from 'logic/itemReducers';
import TooltipBuilder, { colored } from 'tooltips/builder';

import { OverlayContext } from 'ui/utils/OverlayManager';
import Dialog from 'ui/dialog';
import { itemAncient, itemGetCounts, itemGetLimits } from 'logic/item';
import { getTypeIconsPath, Seasons } from 'common';
import EditingContext from 'store/editingContext';

const _L = Locale("ui-equipment.js");

const ItemStatValue = React.forwardRef((props, ref) => <TooltipInput className="d3p-item-stat-value" ref={ref} {...props}/>);

export const ItemSelectItem = React.forwardRef(function SelectItem({ profile, follower, slot, item, onChange, disabled }, ref) {
  const hideLegacy = Data.useOption("hideLegacy");
  const hideCrossClass = Data.useOption("hideCrossClass");
  const charClass = (follower ? profile.follower : profile.class);

  const id = item && item.id;
  const onSetId = React.useCallback(id => {
    onChange((item, profile) => itemSetId(item, id, follower ? profile?.follower : profile?.class));
  }, [onChange, follower]);

  const itemFilter = React.useCallback(item => {
    if (slot && item.classes && !item.classes.includes(charClass)) return false;
    if (item.quality === "ethereal" && profile.seasonal !== Seasons.Ethereal) return false;
    if (item.legacy && hideLegacy) return false;
    if (hideCrossClass && slot) {
      if (item.required && item.required.custom?.id && Data.itemPowerClasses[item.required.custom.id] && Data.itemPowerClasses[item.required.custom.id] !== charClass) {
        return false;
      }
      if (item.set && Data.itemSets[item.set] && Data.itemSets[item.set].tclass && Data.itemSets[item.set].tclass !== charClass) {
        return false;
      }
    }
    return true;
  }, [charClass, slot, hideLegacy, hideCrossClass, profile.seasonal]);

  const isDisabled = React.useMemo(() => {
    if (!id && slot === "offhand") {
      return !Object.keys(Data.getOffhandTypes(profile, follower)).length;
    } else {
      return false;
    }
  }, [profile, slot, id, follower]);

  const getOptions = React.useCallback(() => {
    let types = slot ? Data.itemSlots[slot].types : Data.itemTypes;
    if (slot === "offhand") {
      types = Data.getOffhandTypes(profile, follower);
    }
    const options = [];
    const charClass = (follower ? profile.follower : profile.class);
    const ethereal = [];
    for (const type in types) {
      const typeInfo = Data.itemTypes[type];
      if (type === "custom" || type === "customwpn") continue;
      if (slot && typeInfo.classes && !typeInfo.classes.includes(charClass)) continue;
      const items = typeInfo.items.filter(itemFilter);
      if (!items.length) continue;
      if (typeInfo.ethereal) {
        ethereal.push(...items);
        continue;
      }
      items.sort((a, b) => {
        const aq = (a.quality === "rare" ? "" : a.quality);
        const bq = (b.quality === "rare" ? "" : b.quality);
        if (aq !== bq) return aq.localeCompare(bq);
        const an = a.name.toLowerCase().replace(/^(?:the|a) /, "");
        const bn = b.name.toLowerCase().replace(/^(?:the|a) /, "");
        return an.localeCompare(bn);
      });
      options.push({
        title: genderString(Data.itemTypes[type].name),
        options: items.map(item => item.id),
      });
    }
    if (ethereal.length) {
      ethereal.sort((a, b) => {
        const an = a.name.toLowerCase().replace(/^(?:the|a) /, "");
        const bn = b.name.toLowerCase().replace(/^(?:the|a) /, "");
        return an.localeCompare(bn);
      });
      options.unshift({
        title: _L`Ethereal`,
        options: ethereal.map(item => item.id),
      });
    }
    return options.length === 1 ? options[0].options : options;
  }, [profile, slot, itemFilter, follower]);

  return <ItemSelect
    slot={slot}
    className="d3p-item-item"
    options={getOptions}
    placeholder={_L`Select Item`}
    value={id}
    onChange={onSetId}
    searchLimit={10}
    allowEmpty={true}
    disabled={disabled || isDisabled}
    tabIndex={0}
    ref={ref}
    virtualScroll
    valueQuality
    follower={follower}
  />;
});

export function ItemSelectAncient({ item, onChange }) {
  const seasonal = Store.useProfile(profile => profile.seasonal);
  const charClass = Store.useProfile(profile => profile.class);
  const options = React.useMemo(() => [
    {id: 0, name: _L`Normal`, value: false},
    {id: 1, name: _L`Ancient`, value: true},
    {id: 2, name: _L`Primal Ancient`, value: "primal"},
    seasonal === Seasons.Sanctified && {id: 3, name: _L`Sanctified`, value: "sanctified"},
  ].filter(Boolean), [seasonal]);
  
  const ancient = item && itemAncient(item);
  const itemInfo = Data.itemById[item?.id];
  const immutable = !item || item.imported || itemInfo?.quality === "ethereal" || itemInfo?.type === "primeshard" || itemInfo?.type === "lessershard";

  const onSetAncient = React.useCallback(value => {
    if (value) onChange(item => item && !item.imported && Data.itemById[item.id]?.quality !== "ethereal" ? itemSetAncient(item, value.value, charClass) : item);
  }, [onChange, charClass]);

  const value = item ? (options.find(opt => opt.value === ancient) || options[0]) : null;
  return (
    <Select className="d3p-item-ancient" value={value} options={options} onChange={onSetAncient} disabled={!!immutable} placeholder={_L`Rarity`}/>
  );
}

function itemGetStatOptions(item, charClass, type) {
  let filterClass = charClass;
  if (!filterClass) {
    const itemInfo = Data.itemById[item.id];
    filterClass = itemInfo.class;
    if (!filterClass && itemInfo.set) {
      filterClass = Data.itemSets[itemInfo.set].class;
    }
  }

  const affixes = Data.getItemAffixesById(item.id, item.ancient, false);

  if (type === "socket") {
    return affixes.sockets ? ["sockets"] : [];
  }
  if (type === "caldesanns") {
    return Object.keys(affixes).filter(stat => Data.stats[stat].caldesanns);
  }
  if (type === "sanctified") {
    return Object.keys(affixes).filter(stat => Data.stats[stat].sanctified && (!filterClass || Data.stats[stat].class === filterClass));
  }
  if (type === "shard2") {
    return Object.keys(affixes).filter(stat => {
      if (filterClass && Data.stats[stat].classes && !Data.stats[stat].classes.includes(filterClass)) {
        return false;
      }
      return !Data.stats[stat].shard;
    });
  }
  if (type === "shard3") {
    return Object.keys(affixes).filter(stat => Data.stats[stat].shard);
  }

  return Object.keys(affixes).filter(stat => {
    if (filterClass && Data.stats[stat].classes && !Data.stats[stat].classes.includes(filterClass)) {
      return false;
    }
    if (type === "primary" && Data.stats[stat].secondary) return false;
    if (type === "secondary" && !Data.stats[stat].secondary) return false;
    return true;
  });
}

function renderGemTypeValue(gem) {
  if (gem && gem.id) {
    const item = Data.itemById[gem.id];
    if (!item) return null;
    return <D3TooltipItem data={gem}><span>{item.name} (Rank {item.rank})</span></D3TooltipItem>;
  } else {
    return <span>{Data.legendaryGems[gem] ? Data.legendaryGems[gem].name : Data.gemQualities[gem]}</span>;
  }
}
const renderGemColorValue = id => <span>{Data.gemColors[id].name}</span>;

export function renderGemTypeOption(id, search, gemLevel) {
  if (Data.itemById[id]) {
    const item = Data.itemById[id];
    const icon = Data.itemIcons[item.realid || id];
    return (
      <D3TooltipItem data={id}>
        <div className="d3p-option-gem d3p-quality-legendary">
          {!!icon && <span className="d3p-option-icon" style={{backgroundImage: `url(${getTypeIconsPath(item.type)})`, backgroundPosition: `0 ${-icon[0]}em`}}/>}
          <span>{search ? underlinerFunc(item.name, search) : item.name}</span>
          {item.rank != null && <span className="d3p-gem-rank"> (Rank {item.rank})</span>}
        </div>
      </D3TooltipItem>
    );
  } else if (Data.legendaryGems[id]) {
    const gem = Data.legendaryGems[id];
    const icon = Data.itemIcons[gem.id];
    return (
      <D3TooltipGem id={id} level={Math.min(gem.maxlevel || 150, gemLevel)}>
        <div className="d3p-option-gem d3p-quality-legendary">
          {!!icon && <span className="d3p-option-icon" style={{backgroundImage: `url(${getTypeIconsPath("gemleg")})`, backgroundPosition: `0 ${-icon[0]}em`}}/>}
          <span>{search ? underlinerFunc(gem.name, search) : gem.name}</span>
        </div>
      </D3TooltipGem>
    );
  } else {
    return (
      <div className="d3p-option-gem d3p-option-gem-quality">
        <span className="d3p-option-icon" style={{backgroundImage: `url(${getTypeIconsPath("gems")})`, backgroundPosition: `${-parseInt(id)}em -5em`}}/>
        <span>{search ? underlinerFunc(Data.gemQualities[id], search) : Data.gemQualities[id]}</span>
      </div>
    );
  }
}
export function renderGemColorOption(id, search, gemType) {
  if (!Data.gemQualities[gemType]) return null;
  const gem = Data.gemColors[id];
  const icon = Data.itemIcons[gem.id];
  return (
    <D3TooltipGem id={[gemType, id]}>
      <div className="d3p-option-gem-color">
        {!!icon && <span className="d3p-option-icon" style={{backgroundImage: `url(${getTypeIconsPath("gems")})`, backgroundPosition: `${-gemType}em ${-icon[0]}em`}}/>}
        <span>{search ? underlinerFunc(gem.name, search) : gem.name}</span>
      </div>
    </D3TooltipGem>
  );
}
export function searchGemType(id, search) {
  if (Data.legendaryGems[id]) {
    return Data.legendaryGems[id].name.match(search);
  } else if (Data.gemQualities[id]) {
    return Data.gemQualities[id].match(search);
  }
}

function ItemGemLine({ item, index, onChange, charClass, editGem }) {
  const [focusIdx, setFocus] = React.useState(0);

  const seasonal = Store.useProfile(profile => profile.seasonal);

  const gem = item.gems && item.gems[index];
  const itemType = Data.itemById[item.id].type;
  const getGemOptions = React.useCallback(() => {
    const slotType = Data.itemTypes[itemType] && Data.itemTypes[itemType].slot;

    const soulShards = {
      head: 'primeshard',
      mainhand: 'lessershard',
      onehand: 'lessershard',
      twohand: 'lessershard',
    }[slotType];
    let result = [];
    if (seasonal === Seasons.SoulShards && soulShards) {
      result.push({
        title: _L`Soul Shards`,
        options: Data.itemTypes[soulShards]?.items.slice().sort((a, b) => {
          if (a.rank === b.rank) return a.name.localeCompare(b.name);
          return b.rank - a.rank;
        }).map(item => item.id),
      });
    }
    const legGems = {
      title: _L`Legendary Gems`,
      options: Object.keys(Data.legendaryGems).filter(id => {
        const info = Data.legendaryGems[id];
        return info.types && info.types.includes(slotType);
      }),
    };
    if (slotType !== "head") {
      result.push(legGems);
    }
    result.push({
      title: _L`Normal Gems`,
      options: Data.gemQualities.map((q, i) => i).reverse(),
    });
    if (slotType === "head") {
      result.push(legGems);
    }
    result = result.filter(g => g?.options?.length);

    return result.length > 1 ? result : result[0]?.options || [];
  }, [itemType, seasonal]);

  const gemLevel = gem && Array.isArray(gem) && Data.legendaryGems[gem[0]] && gem[1];
  const renderGemTypeOption_ = React.useCallback((id, search) => renderGemTypeOption(id, search, gemLevel), [gemLevel]);

  const gemType = gem && Array.isArray(gem) && !Data.legendaryGems[gem[0]] && parseInt(gem[0]);
  const renderGemColorOption_ = React.useCallback((id, search) => renderGemColorOption(id, search, gemType), [gemType]);

  const valueRef = React.useRef();
  React.useEffect(() => {
    if (focusIdx && valueRef.current) {
      valueRef.current.focus();
    }
  }, [focusIdx]);

  const onSetType = React.useCallback(type => {
    onChange(item => itemSetGemType(item, index, type, charClass));
    setFocus(value => value + 1);
  }, [onChange, index, charClass]);
  const onSetLevel = React.useCallback(value => onChange(item => itemSetGemLevel(item, index, value)), [onChange, index]);
  const onSetColor = React.useCallback(color => onChange(item => itemSetGemColor(item, index, color)), [onChange, index]);

  return (
    <li>
      <div className="d3p-item-stat-half-line">
        <Select className="d3p-item-gem-type ui-Select-noframe"
                options={getGemOptions}
                placeholder={_L`Empty Socket`}
                value={gem && (Array.isArray(gem) ? gem[0] : gem)}
                onChange={onSetType}
                searchLimit={11}
                searchMiddle={true}
                renderOption={renderGemTypeOption_}
                renderValue={renderGemTypeValue}
                searchFunc={searchGemType}
                allowEmpty={true}
        />
      </div>
      <div className="d3p-item-stat-half-line">
        {!!(gem && Data.legendaryGems[gem[0]]) && (
          <TooltipInput className="d3p-item-gem-level"
                        min={0}
                        max={Data.legendaryGems[gem[0]].maxlevel || 150}
                        step={1}
                        value={gem[1]}
                        onChange={onSetLevel}
                        ref={valueRef}
          />
        )}
        {!!(gem && Data.legendaryGems[gem[0]]) && (
          <div className="d3p-item-gem-level"/>
        )}
        {!!(gem && !Data.legendaryGems[gem[0]] && Data.gemQualities[gem[0]]) && (
          <Select className="d3p-item-gem-color ui-Select-noframe"
                  options={Object.keys(Data.gemColors)}
                  value={gem[1]}
                  onChange={onSetColor}
                  renderOption={renderGemColorOption_}
                  renderValue={renderGemColorValue}
                  ref={valueRef}
          />
        )}
        {!!(gem && Data.itemById[gem.id]) && (
          <button className="d3p-item-gem-edit" onClick={editGem}>{_L`Edit`}</button>
        )}
        {!!(gem && Data.itemById[gem.id]) && (
          <div className="d3p-item-gem-level"/>
        )}
      </div>
    </li>
  );
}

function ItemGems({ item, onChange, charClass, editGem }) {
  if (!item.gems) return null;
  return (
    <ul className="d3p-item-gem-list">
      {item.gems.map((_, index) => <ItemGemLine key={index} item={item} index={index} onChange={onChange} charClass={charClass} editGem={editGem}/>)}
    </ul>
  );
}

const renderPassiveOption = (id, search) => {
  if (!Data.allPassives[id]) return null;
  const [charClass, passive] = Data.allPassives[id];
  return (
    <D3TooltipSkill charClass={charClass} skill={passive}>
      <div className="d3p-option-passive">
        <span className={`d3p-option-icon d3p-passive-icon d3p-class-${charClass}`} style={{backgroundPosition: `${-passive.index}em 0`}}/>
        <span>{search ? underlinerFunc(passive.name, search) : passive.name}</span>
      </div>
    </D3TooltipSkill>
  );
};
const renderPassiveValue = id => <span>{Data.allPassives[id]?.[1].name || ""}</span>;
const searchPassive = (id, search) => Data.allPassives[id]?.[1].name.match(search);

const ItemStatPassive = React.memo(function ItemStatPassive({ charClass, value, onChange, exclude }) {
  const getOptions = React.useCallback(() => {
    if (charClass) {
      return Object.keys(Data.passives[charClass]).filter(id => !exclude?.includes(id));
    } else {
      return Object.keys(Data.classes).filter(cls => Data.passives[cls]).map(cls => ({
        title: Data.classes[cls].name,
        options: Object.keys(Data.passives[cls]).filter(id => !exclude?.includes(id)),
      }));
    }
  }, [charClass, exclude]);

  return (
    <Select className="d3p-item-stat-passive"
            options={getOptions}
            value={value}
            onChange={onChange}
            searchLimit={10}
            searchMiddle={true}
            renderOption={renderPassiveOption}
            renderValue={renderPassiveValue}
            searchFunc={searchPassive}
    />
  );
});

const renderStatOption = ({id, name, disabled, item}, search) => {
  if (item) {
    return (
      <D3TooltipItem data={id}>
        <div className="d3p-quality-legendary">{Select.defaultCallbacks.renderOption(name, search)}</div>
      </D3TooltipItem>
    );
  }
  if (!disabled) return Select.defaultCallbacks.renderOption(name, search);
  let tip;
  if (id === disabled) {
    tip = (
      <p>
        <span className="d3-color-gold">{_L`Already present on the item.`}</span>
      </p>
    );
  } else if (Data.stats[disabled]) {
    tip = (
      <p>
        <span className="d3-color-gold">{_L`Conflicts with:`}</span><br/>
        <span className="tooltip-icon-bullet"/>{Data.stats[disabled].name}
      </p>
    );
  } else if (disabled === "_resall") {
    tip = (
      <p>
        <span className="d3-color-gold">{_L`Conflicts with preset stat:`}</span><br/>
        <span className="tooltip-icon-bullet"/>{Data.stats.resall.name}
      </p>
    );
  } else {
    return Select.defaultCallbacks.renderOption(name, search);
  }
  return (
    <Tooltip tooltip={tip}>
      <div>{Select.defaultCallbacks.renderOption(name, search)}</div>
    </Tooltip>
  )
};

const ItemStatLine = React.forwardRef(function ItemStatLine({ item, onChange, charClass, stat, limits, type, setFocus, editGem }, ref) {
  const getOptions = React.useCallback(() => {
    if (limits && limits.required) {
      const itemInfo = Data.itemById[item.id];
      if (stat === "custom" && itemInfo.required?.custom?.options) {
        return itemInfo.required.custom.options.map(id => ({
          id,
          name: Data.itemById[id]?.name,
          item: true,
        })).filter(opt => opt.name).sort((a, b) => a.name.localeCompare(b.name));
      } else {
        return [];
      }
    }

    if (type === "shard3") {
      return itemGetStatOptions(item, charClass, type).map(stat => ({id: stat, name: Data.stats[stat].name}));
    }

    if (type === 'sanctified') {
      return itemGetStatOptions(item, charClass, type).map(stat => ({id: stat, name: Data.stats[stat].name}));
    }

    const statSet = new Set(itemGetStatOptions(item, charClass, type));
    if (!statSet.size) return [];

    const affixes = Data.getItemAffixesById(item.id, item.ancient, true);
    const used = {};
    for (const stat of Object.keys(item.stats)) {
      if (!Data.stats[stat]) continue;
      if (affixes[stat] && affixes[stat].noblock && !(item.merged && item.merged[stat])) {
        continue;
      }
      used[Data.stats[stat].group || stat] = stat;
    }
    const presets = Data.getItemPreset(item.id);
    if (presets.includes("resall")) {
      for (const stat of Data.extendStats([], "resist")) {
        if (!used[stat]) {
          used[stat] = "_resall";
        }
      }
    }

    const curStat = (Data.stats[stat] ? (Data.stats[stat].group || stat) : null);

    const results = [];
    for (const [catType, catList] of Object.entries(Data.statList)) {
      for (const [cat, list] of Object.entries(catList)) {
        const options = Data.extendStats([], list).filter(stat => statSet.has(stat)).map(stat => {
          const subStat = Data.stats[stat].group || stat;
          const opt = {id: stat, name: Data.stats[stat].name};
          if (subStat !== curStat && (used[subStat] || used[stat])) {
            opt.disabled = used[subStat] || used[stat];
          }
          return opt;
        });
        if (options.length) {
          results.push({
            title: cat + (catType === "secondary" && type !== "secondary" ? _L` (Secondary)` : ""),
            options,
          });
        }
      }
    }
    return results.length === 1 ? results[0].options : results;
  }, [item, charClass, stat, limits, type]);

  const onSetStat = React.useCallback(value => {
    onChange(item => itemChangeStat(item, stat, value.id));
    setFocus({stat: stat !== "custom" ? value.id : stat});
  }, [onChange, stat, setFocus]);
  const onSetValue1 = React.useCallback(value => onChange(item => itemSetStatValue(item, stat, 0, value)), [onChange, stat]);
  const onSetValue2 = React.useCallback(value => onChange(item => itemSetStatValue(item, stat, 1, value)), [onChange, stat]);
  const onSetPassive = React.useCallback(value => onChange(item => itemSetStatValue(item, stat, 0, value)), [onChange, stat]);

  const itemInfo = Data.itemById[item.id];
  let value = null;
  if (stat) {
    value = {id: stat};
    if (stat === "custom" && itemInfo.required?.custom) {
      if (itemInfo.required.custom.options) {
        const otherItem = Data.itemById[item.customId || itemInfo.required.custom.options[0]];
        if (otherItem?.required?.custom) {
          value.id = item.customId;
          value.name = otherItem.required.custom.name;
        } else {
          value = null;
        }
      } else {
        value.name = itemInfo.required.custom.name;
      }
    } else {
      value.name = Data.stats[stat].name;
    }
  }

  let statType = type || "primary";
  if (Data.stats[stat]) {
    const itemData = Data.itemById[item.id];
    if (stat === "sockets") {
      statType = "socket";
    } else if (Data.stats[stat].secondary && itemData?.quality !== "ethereal" && itemData?.type !== "primeshard" && itemData?.type !== "lessershard") {
      statType = "secondary";
    } else {
      statType = "primary";
    }
  }

  const canRemove = (item.imported ? (stat && (type === "caldesanns" || type === "socket")) : (limits ? !limits.required : !type)) && type !== 'sanctified';
  const onRemove = React.useCallback(() => onChange(item => itemChangeStat(item, stat, null)), [onChange, stat]);
  const isEnchanted = (stat && item.enchant === stat);

  const inputRef = React.useRef();
  React.useImperativeHandle(ref, () => ({
    focus() {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }), []);

  const isLocked = item.imported && type !== "socket" && ((item.enchant && stat && stat !== item.enchant && type !== "caldesanns") || (limits && limits.required));
  const isOffhandDamage = (itemInfo.type === "source" || itemInfo.type === "mojo") && stat === "wpnphy";

  return (
    <li className={statType ? "d3p-stat-" + statType : null}>
      <div className="d3p-item-stat-line">
        <div className="d3p-item-stat-half-line">
          {!!canRemove && (
            <span className="d3p-item-stat-remove" title={_L`Remove Stat`} onClick={onRemove}>
              <FontAwesomeIcon icon={faTimes}/>
            </span>
          )}
          {!!isEnchanted && (
            <span className="d3p-item-stat-enchanted" title={_L`Enchanted`}>
              <FontAwesomeIcon icon={faSyncAlt}/>
            </span>
          )}
          <Select className="d3p-item-stat-list ui-Select-noframe"
                  options={getOptions}
                  disabled={isLocked || (limits && limits.required && (stat !== "custom" || !itemInfo.required?.custom?.options))}
                  placeholder={_L`Select a Stat`}
                  value={value}
                  onChange={onSetStat}
                  searchLimit={10}
                  searchMiddle={true}
                  renderOption={renderStatOption}
                  ref={stat ? null : inputRef}
          />
        </div>
        <div className="d3p-item-stat-half-line">
          {!!(limits && limits.args >= 1) && <ItemStatValue disabled={isLocked && !isOffhandDamage} min={limits.min} max={limits.max} step={limits.step} value={item.stats[stat][0]} onChange={onSetValue1} ref={inputRef}/>}
          {!!(limits && limits.args === 1) && <div className="d3p-item-stat-value"/>}
          {!!(limits && limits.args >= 2) && <ItemStatValue disabled={isLocked && !isOffhandDamage} min={limits.min2} max={limits.max2} step={limits.step2} value={item.stats[stat][1]} onChange={onSetValue2}/>}
          {!!(limits && limits.args < 0) && <ItemStatPassive disabled={isLocked} charClass={charClass} value={item.stats[stat][0]} exclude={limits.exclude} onChange={onSetPassive}/>}
        </div>
      </div>
      {stat === "sockets" && <ItemGems item={item} onChange={onChange} charClass={charClass} editGem={editGem}/>}
    </li>
  );
});

function useFilterClass() {
  const follower = (EditingContext.useStateView("paperdollMode") === "follower");
  return Store.useSelector(state => {
    if (Data.getOption("limitStats", state)) {
      const profile = state.profiles[state.activeProfile];
      return (follower ? profile.follower : profile.class);
    } else {
      return null;
    }
  }, [follower]);
}

function ItemWarnings({ warnings }) {
  const tip = React.useMemo(() => {
    const tip = new TooltipBuilder(_L`Impossible stat combination`);
    for (const line of warnings) {
      if (typeof line === "string") {
        tip.line(line);
      } else {
        tip.line(_L`More than one preset stat is missing`);
        for (const stat of line.presets) {
          let name = stat;
          if (Data.stats[stat]) {
            name = Data.stats[stat].name;
          } else if (Data.statGroupNames[stat]) {
            name = Data.statGroupNames[stat];
          } else if (stat.match(/^skill_/)) {
            name = _L`Skill Damage`;
          }
          tip.line(colored(line.missing.includes(stat) ? name : <s>{name}</s>, "white"), false);
        }
      }
    }
    return tip.build();
  }, [warnings]);

  return (
    <Tooltip tooltip={tip} touchAction="show">
      <span className="d3p-item-warning">
        <FontAwesomeIcon icon={faExclamationTriangle}/>
      </span>
    </Tooltip>
  );
}

function ItemStats({ item, onChange, editGem }) {
  const [focusStat, setFocusStat] = React.useState(null);
  
  const limits = React.useMemo(() => itemGetLimits(item), [item]);
  const charClass = useFilterClass();
  const moreWarnings = Data.useOption("moreWarnings");

  const focusRef = React.useRef();

  const {stats, warnings} = React.useMemo(() => {
    const itemInfo = Data.itemById[item.id];
    if (!itemInfo) return [];
    const slot = Data.itemTypes[itemInfo.type] ? Data.itemTypes[itemInfo.type].slot : null;
    const isShard = itemInfo.type === "primeshard" || itemInfo.type === "lessershard";
    const stats = Object.keys(limits).filter(stat => Data.stats[stat]).map(stat => {
      const secondary = itemInfo.quality !== "ethereal" && Data.stats[stat].secondary;
      const info = {
        key: stat,
        stat,
        limits: limits[stat],
        prio: (stat === "sockets" ? 4 : (secondary ? 2 : 0)),
        subprio: Data.stats[stat].prio,
      };
      if (isShard) {
        info.type = "primary";
        if (!itemInfo.required[stat]) {
          if (Data.stats[stat]?.shard) {
            info.type = "shard3";
          } else {
            info.type = "shard2";
          }
        }
      }
      else if ((slot === "twohand" || slot === "onehand") && stat === "sockets") info.type = "socket";
      else if (Data.stats[stat].sanctified) {
        info.type = 'sanctified';
        info.prio = 3;
        info.subprio = 1;
      }
      else if (secondary) info.type = "secondary";
      else if (item.imported && Data.stats[stat].caldesanns) info.type = "caldesanns";
      else info.type = "primary";
      if (focusStat && focusStat.stat === stat) info.ref = focusRef;
      return info;
    });

    const counts = itemGetCounts(item);

    const warnings = [];
    if (counts.usedPrimary > counts.maxPrimary) {
      warnings.push(_L`Too many primary stats`);
    } else if (counts.usedPrimary < counts.maxPrimary && moreWarnings) {
      warnings.push(_L`Add more primary stats`);
    }
    if (counts.usedSecondary > counts.maxSecondary) {
      warnings.push(_L`Too many secondary stats`);
    } else if (counts.usedSecondary < counts.maxSecondary && moreWarnings) {
      warnings.push(_L`Add more secondary stats`);
    }
    const affixes = Data.getItemAffixesById(item.id, item.ancient, true);
    const presets = Data.getItemPreset(item.id);
    const missing = presets.filter(stat => {
      const list = Data.smartListStats(stat, affixes);
      return list.length && list.every(stat => !item.stats[stat]);
    });
    if (missing.length > 1) {
      warnings.push({presets, missing});
    }

    const ancient = itemAncient(item);
    const immutable = item.imported || itemInfo.quality === "ethereal" || isShard;

    let empty = item.empty || 0;
    let emptyPrimary = 0;
    if (immutable) {
      if (ancient && !counts.caldesanns) {
        stats.push({key: "caldesanns", type: "caldesanns", prio: 1, subprio: 0});
      }
      if (ancient === 'sanctified') {
        stats.push({key: "sanctified", type: "sanctified", prio: 3, subprio: 1});
      }
    } else {
      while (counts.usedPrimary < counts.maxPrimary) {
        stats.push({key: `primary${counts.usedPrimary}`, type: "primary", prio: 1, subprio: 0});
        counts.usedPrimary += 1;
        empty -= 1;
        emptyPrimary += 1;
      }
      while (counts.usedSecondary < counts.maxSecondary) {
        stats.push({key: `secondary${counts.usedSecondary}`, type: "secondary", prio: 3, subprio: 0});
        counts.usedSecondary += 1;
        empty -= 1;
      }
    }
    if ((slot === "twohand" || slot === "onehand") && !item.stats.sockets) {
      stats.push({key: "sockets", type: "socket", prio: 5, subprio: 0});
      empty -= 1;
    }
    if (empty > 0 && !immutable) {
      while (empty > 0) {
        empty -= 1;
        emptyPrimary += 1;
        stats.push({key: `empty${empty}`, prio: 1, subprio: 1});
      }
      if (focusStat && !focusStat.stat) {
        stats[stats.length - 1].ref = focusRef;
      }
    }
    if (!immutable && !emptyPrimary && ancient && !counts.caldesanns) {
      stats.push({key: "caldesanns", type: "caldesanns", prio: 1, subprio: 0});
    }
    if (!immutable && ancient === 'sanctified' && !counts.sanctified) {
      stats.push({key: "sanctified", type: "sanctified", prio: 4, subprio: 0});
    }

    stats.sort((a, b) => (a.prio !== b.prio ? a.prio - b.prio : a.subprio - b.subprio));

    return {stats, warnings};
  }, [item, limits, focusStat, moreWarnings]);

  React.useEffect(() => {
    if (focusRef.current) {
      focusRef.current.focus();
    }
  }, [focusStat]);

  const onAddStat = React.useCallback(() => {
    onChange(item => itemAddStat(item));
    setFocusStat({stat: null});
  }, [onChange]);

  const itemInfo = Data.itemById[item.id];
  const immutable = item.imported || itemInfo?.quality === "ethereal" || itemInfo?.type === "primeshard" || itemInfo?.type === "lessershard";
  return (
    <div>
      <ul className="d3p-item-stats">
        {stats.map(info => <ItemStatLine key={info.key} item={item} onChange={onChange} charClass={charClass} stat={info.stat} limits={info.limits} type={info.type} ref={info.ref} setFocus={setFocusStat} editGem={editGem}/>)}
      </ul>
      {!immutable && (
        <div className="d3p-item-add-stat" onClick={onAddStat}>
          <span className="d3p-item-plus">
            <FontAwesomeIcon icon={faPlus}/>
          </span>
          {_L`Add stat`}
        </div>
      )}
      {!immutable && warnings.length > 0 && <ItemWarnings warnings={warnings}/>}
    </div>
  );
}

function ItemLockDialog({ stats, onResolve, onClose }) {
  const [stat, setStat] = React.useState(null);
  const options = React.useMemo(() => [
    {id: null, name: _L`Not Enchanted`},
    ...stats.map(stat => ({id: stat, name: Data.stats[stat].name})),
  ], [stats]);

  const onChangeStat = React.useCallback(stat => setStat(stat && stat.id), []);
  const onAccept = React.useCallback(() => onResolve({stat}), [stat, onResolve]);

  return (
    <Dialog title={_L`Lock item`} modal onClose={onClose} style={{width: 280}}>
      <Dialog.Content>
        <p className="d3p-tip">{_L`Locking an item allows only one stat to be enchanted, which can be useful for optimizing your gear when you can't import it from your profile.`}</p>
        <div>{_L`Enchant: ${<Select options={options} value={stat && {id: stat, name: Data.stats[stat].name}} onChange={onChangeStat} placeholder={_L`Not Enchanted`} allowEmpty/>}`}</div>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={onAccept}>{_L`Lock`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

function ImportResetTooltip({ item, slot, ...props }) {
  if (!item.imported) return null;
  const unlocked = {...item};
  unlocked.stats = unlocked.imported.stats;
  unlocked.enchant = unlocked.imported.enchant;
  return <D3TooltipItem data={unlocked} slot={slot} {...props}/>;
}

const ItemEditor = React.memo(React.forwardRef(function ItemEditor({ slot, item, onChange, className, editGem }, ref) {
  const itemInfo = item && Data.itemById[item.id];
  const overlays = React.useContext(OverlayContext);

  const onLock = React.useCallback(async e => {
    if (!item || item.imported) return;
    const affixes = Data.getItemAffixesById(item.id, item.ancient, false);
    const required = Data.getItemAffixesById(item.id, item.ancient, "only");
    const stats = Object.keys(item.stats).filter(stat => {
      if (!Data.stats[stat] || Data.stats[stat].base || Data.stats[stat].caldesanns) return false;
      if (!affixes[stat] || affixes[stat].args < 0) return false;
      if (required[stat] && (!item.merged || !item.merged[stat])) return false;
      return true;
    });
    const result = await overlays.async(<ItemLockDialog stats={stats}/>);
    if (result) {
      onChange(item => itemSetImported(item, result.stat));
    }
  }, [item, overlays, onChange]);

  const onReset = React.useCallback(() => onChange(item => itemImportReset(item)), [onChange]);
  const onUnlock = React.useCallback(() => onChange(item => itemImportUnlock(item)), [onChange]);

  return (
    <div className={classNames("d3p-ItemEditor", className)}>
      {!!(item && item.imported) && (
        <div className="d3p-item-imported">
          <span className="d3p-item-lock">
            <FontAwesomeIcon icon={faLock}/>
          </span>
          {_L`Imported item (${
            <ImportResetTooltip item={item} slot={slot}>
              <span onClick={onReset}>{_L`reset`}</span>
            </ImportResetTooltip>
          }, ${<span onClick={onUnlock}>{_L`unlock`}</span>})`}
        </div>
      )}
      {!!itemInfo && <ItemStats item={item} onChange={onChange} editGem={editGem}/>}
      {!!(item && !item.imported && itemInfo?.quality !== "ethereal" && itemInfo?.type !== "primeshard" && itemInfo?.type !== "lessershard") && (
        <div className="d3p-item-unimport" onClick={onLock}>
          <span className="d3p-item-lock">
            <FontAwesomeIcon icon={faLock}/>
          </span>
          {_L`Lock item`}
        </div>
      )}
    </div>
  );
}));

export default ItemEditor;
